import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getReviewAndPayCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getReviewAndPayPromotions } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import clearMessages from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/ClearMessages";
import loadDataIfNeeded from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/LoadDataIfNeeded";
import notifyProductRecs from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/NotifyProductRecs";
import setCartId from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetCartId";
import setPlaceOrderErrorMessage from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetPlaceOrderErrorMessage";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import AddressErrorModal from "@insite/content-library/Components/AddressErrorModal";
import Page from "@insite/mobius/Page";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps extends PageProps {}
type MyState = { validationError: any };

export const ErrorContext = React.createContext({
    state: {
        collectAccountErrors: false,
    },
    updateError: (key: any, val: any) => {},
});

const mapDispatchToProps = {
    setCartId,
    setPlaceOrderErrorMessage,
    loadDataIfNeeded,
    clearMessages,
    notifyProductRecs,
};

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = getQueryStrings(state);
    const cartState = getReviewAndPayCartState(state);
    const promotionsDataView = getReviewAndPayPromotions(state);
    const expand = state.data.carts.cartApiParameter?.expand || [];

    const paymentMethod = cartState.value?.paymentMethod;
    const isBnplTokensMissing =
        paymentMethod?.isBnpl === true &&
        (!paymentMethod?.bnplTransactionToken || !paymentMethod?.bnplClientSecret) &&
        !cartState.isLoading;

    const isLoadDataNeeded =
        ((!cartState.value ||
            (cartState.value.lineCount && !cartState.value.cartLines?.length) ||
            !cartState.value.carriers ||
            !expand.includes("shipping") ||
            !expand.includes("tax")) &&
            !cartState.isLoading) ||
        (!promotionsDataView.value && !promotionsDataView.isLoading) ||
        isBnplTokensMissing;

    return {
        cartId,
        cartState, // do not delete this, needed to trigger cart loading
        isLoadDataNeeded: isLoadDataNeeded || !getCurrentCountries(state),
        productRecsCalled: state.pages.checkoutReviewAndSubmit.productRecsCalled,
    };
};

type Props = PageProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

const CheckoutReviewAndSubmitPage = ({
    id,
    cartId,
    isLoadDataNeeded,
    productRecsCalled,
    setCartId,
    loadDataIfNeeded,
    setPlaceOrderErrorMessage,
    clearMessages,
    notifyProductRecs,
}: Props) => {
    const [validationError, setValidationError] = useState({
        collectAccountErrors: false,
    });

    useEffect(() => {
        if (isLoadDataNeeded) {
            loadDataIfNeeded({ cartId, cartLinesShouldBeExpanded: true });
        } else if (!productRecsCalled) {
            notifyProductRecs({ cartId });
        }
    });

    useEffect(() => {
        setCartId({ cartId });
        setPlaceOrderErrorMessage({});
        clearMessages();
    }, []);

    const updateError = (key: any, val: any) => {
        const erros = {
            ...validationError,
            [key]: val,
        };
        setValidationError(erros);
    };

    return (
        <Page data-test-selector="checkoutReviewAndSubmitPage">
            <ErrorContext.Provider value={{ state: validationError, updateError }}>
                <Zone zoneName="Content" contentId={id} />
                <AddressErrorModal />
            </ErrorContext.Provider>
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutReviewAndSubmitPage),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "CheckoutReviewAndSubmitPage" instead of this constant.
 */
export const CheckoutReviewAndSubmitPageContext = "CheckoutReviewAndSubmitPage";
